// DashboardLayout.js
import { Link, Outlet, useLocation } from "react-router-dom";
import Header from "../components/header/Header";
import styles from "./dashboard-layout.module.css";
import { Suspense, useEffect, useRef, useState } from "react";
import Sidebar from "../components/sidebar/Sidebar";
import PageSkeleton from "../components/page-skeleton/PageSkeleton";
import RouteErrorBoundary from "../components/route-error-boundary/RouteErrorBoundary";
import { OverlayCanvasProvider } from "../components/overlay-portal/OverlayPortal";
import { useUser } from "../../app/providers/userContext";
import { isSubscriptionRestricted } from "../constants/subscriptionAccess";
import SubscriptionNotice from "../../features/billing/components/SubscriptionNotice";

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user } = useUser();
  const mainContentRef = useRef<HTMLElement>(null);

  useEffect(() => {
    mainContentRef.current?.scrollTo({ top: 0, behavior: "auto" });
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (window.innerWidth >= 992 || !sidebarOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const breadcrumbItems = location.pathname
    .split("/")
    .filter(Boolean)
    .map((segment, index, segments) => ({
      label: segment.replace(/[-_]/g, " "),
      path: `/${segments.slice(0, index + 1).join("/")}`,
    }));

  const formatBreadcrumb = (value: string) =>
    /^\d+$/.test(value)
      ? "Details"
      : value.replace(/\b\w/g, (letter: string) => letter.toUpperCase());

  const homePath = breadcrumbItems[0]?.path
    ? `${breadcrumbItems[0].path}/dashboard`
    : "/";

  const role = breadcrumbItems[0]?.label === 'student' ? 'STUDENT' : user?.roles?.[0]?.toUpperCase();
  const isDashboard = /\/dashboard$/.test(location.pathname);
  const subscriptionRestricted = isSubscriptionRestricted(user);
  const priorityLinks = role === 'STUDENT'
    ? []
    : role === 'TEACHER'
      ? [
          { label: 'My classes', path: '/teacher/classes' },
          { label: 'Mark attendance', path: '/teacher/students/attendance' },
          { label: 'View assignments', path: '/teacher/assignment' },
        ]
      : role === 'SUPER_ADMIN'
        ? [
            { label: 'Review schools', path: '/super_admin/school' },
            { label: 'Check monitoring', path: '/super_admin/monitor' },
          ]
        : [
            { label: 'Manage students', path: '/admin/student' },
            { label: 'Review fees', path: '/admin/fees' },
            { label: 'Check attendance', path: '/admin/attendance' },
          ];

  return (
     <OverlayCanvasProvider>
    <div className={`${styles.dashboardContainer} d-flex`}>
      <a className={styles.skipLink} href="#main-content">Skip to content</a>
      <Sidebar
        isOpen={sidebarOpen} 
        toggleSidebar={toggleSidebar} 
      />
      <div className={`${styles.dashboardContent} flex-grow-1`}>
        <Header toggleSidebar={toggleSidebar} />
        <nav className={`${styles.breadcrumbNav} text-base text-gray-600`} aria-label="Breadcrumb">
          <ol className="mb-0 flex flex-wrap items-center gap-x-2 gap-y-1">
            <li>
              <Link className="font-medium text-blue-700 hover:text-blue-900 hover:underline" to={homePath}>
                Home
              </Link>
            </li>
            {breadcrumbItems.map((item, index) => (
              <li key={`${item.path}-${index}`} className="flex items-center gap-2">
                <span aria-hidden="true">/</span>
                {index === breadcrumbItems.length - 1 ? (
                  <span className="font-semibold text-gray-900" aria-current="page">
                    {formatBreadcrumb(item.label)}
                  </span>
                ) : (
                  <Link className="font-medium text-blue-700 hover:text-blue-900 hover:underline" to={item.path}>
                    {formatBreadcrumb(item.label)}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </nav>
        {isDashboard && !subscriptionRestricted && priorityLinks.length > 0 && (
          <section className={`${styles.priorityActions} flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white`} aria-label="Priority actions">
            <span className="mr-1 text-sm font-semibold text-slate-700">Quick access</span>
            {priorityLinks.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {item.label}
              </Link>
            ))}
          </section>
        )}
        <main ref={mainContentRef} id="main-content" className={`${styles.mainContent} w-full max-w-full overflow-x-hidden`}>
          <RouteErrorBoundary>
            <Suspense fallback={<PageSkeleton />}>
              {isDashboard && subscriptionRestricted ? <SubscriptionNotice /> : <Outlet />}
            </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>
    </div>
  </OverlayCanvasProvider>
  );
};

export default DashboardLayout;
